/**
 * Admin stats (§9): signups over time, active users, entity counts.
 *
 * The card grid with big semantic numbers is DragDuro's admin home, carried
 * over: colour is meaning, not decoration, and the number is the message.
 */

import type { StatsResponse } from '@svcdue/contracts';

import { useAdminStats } from '../api/hooks';
import { ErrorState, Loading } from '../components/states';
import { BarChart } from './BarChart';
import type { BarDatum } from './BarChart';

export function AdminStats({ days }: { days: number }) {
  const query = useAdminStats(days, true);

  if (query.isPending) return <Loading label="Loading stats…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return null;

  return <StatsView data={query.data} days={days} />;
}

export function StatsView({ data, days }: { data: StatsResponse; days: number }) {
  const { totals, activeUsers } = data;

  const bars: BarDatum[] = data.signups.map((s) => ({
    date: s.date,
    value: s.count,
    label: `${s.date}: ${s.count} signup${s.count === 1 ? '' : 's'}`,
  }));

  const totalSignups = data.signups.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="admin-panel">
      <div className="stat-grid">
        <Stat label="Users" value={totals.users} hint="have signed in" />
        <Stat label="Garages" value={totals.garages} hint={`${totals.publicGarages} public`} tone="amber" />
        <Stat label="Cars" value={totals.cars} hint={`${totals.drivers} drivers`} />
        <Stat label="Parts" value={totals.items} hint="tracked items" />
        <Stat label="Events" value={totals.events} hint={`${totals.serviceLogs} service logs`} />
      </div>

      <div className="card admin-card">
        <div className="admin-h">
          <div>
            <h2 className="h2">Signups</h2>
            {/**
             * The series is zero-filled server-side, so a flat stretch is a
             * genuinely quiet week rather than missing data — worth saying,
             * because a chart with no gaps invites the opposite reading.
             */}
            <span className="admin-sub">
              {totalSignups} in the last {days} days
            </span>
          </div>
        </div>
        <BarChart data={bars} format={(v) => String(Math.round(v))} />
      </div>

      <div className="card admin-card">
        <div className="admin-h">
          <div>
            <h2 className="h2">Active users</h2>
            {/**
             * "Active" is a specific claim here and the definition is not
             * obvious, so it is stated rather than assumed: the API counts
             * users who *logged a service or an event* in the window
             * (contracts/admin.ts). It is not sign-ins — those are not
             * recorded at all — so this reads lower than a typical MAU figure
             * and means something stronger.
             */}
            <span className="admin-sub">logged a service or an event in the window</span>
          </div>
        </div>
        <div className="stat-grid stat-grid-2">
          <Stat label="Last 7 days" value={activeUsers.last7Days} tone="green" />
          <Stat label="Last 30 days" value={activeUsers.last30Days} tone="green" />
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: 'amber' | 'green' | 'red';
}) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={'stat-n' + (tone ? ' stat-' + tone : '')}>{value.toLocaleString('en-US')}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}
